/**
 * Die Wege durch die App.
 *
 * Nutzersichtbare Pfade sind deutsch, Bezeichner im Code englisch — dieselbe
 * Aufteilung wie im Backend (`engine.ts`: englische Funktionsnamen, deutsche
 * Kommentare und deutsche Feldnamen nach aussen).
 *
 * WARUM KEIN LAZY LOADING: sieben kleine Seiten. Ein aufgeteiltes Bundle
 * brauchte fuer jede davon einen zweiten Netzabruf, und zwar genau in dem
 * Moment, in dem jemand sich anmelden will.
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { SessionProvider, useSitzung } from './session';
import { Kreisel } from './ui';
import Abgemeldet from './seiten/Abgemeldet';
import Newsletter from './seiten/Newsletter';
import Admin from './seiten/Admin';
import AdminKonto from './seiten/AdminKonto';
import Anleitung from './seiten/Anleitung';
import Anmelden from './seiten/Anmelden';
import Bericht from './seiten/Bericht';
import Bestaetigt from './seiten/Bestaetigt';
import Konto from './seiten/Konto';
import Kuendigen from './seiten/Kuendigen';
import Laeufe from './seiten/Laeufe';
import LaufDetail from './seiten/LaufDetail';
import Personas from './seiten/Personas';
import PasswortNeu from './seiten/PasswortNeu';
import PasswortVergessen from './seiten/PasswortVergessen';
import Registrieren from './seiten/Registrieren';
import Schluessel from './seiten/Schluessel';
import Verbinden from './seiten/Verbinden';
import Fragen, { MitFragen } from './seiten/Fragen';
import Einwilligung from './Einwilligung';
import { STANDARDZIEL } from './ziel';
import { t, useSprache } from './sprache';
import { SpracheAbgleich } from './sprache-wahl';

/**
 * Riegel vor Seiten, die eine Sitzung brauchen.
 *
 * Der Zustand „laedt" bekommt eine eigene Ausgabe, statt als „abgemeldet"
 * durchzugehen: sonst wird jeder, der die Seite neu laedt, fuer einen Wimpern-
 * schlag auf die Anmeldung geworfen und verliert, wo er war.
 *
 * Eine Sitzung aus einem Wiederherstellungslink zaehlt hier NICHT als
 * Anmeldung. Sie ist der Nachweis fuer genau einen Vorgang: ein Passwort
 * setzen. Wer mit ihr in die App liefe, waere durch einen Maillink angemeldet,
 * ohne sein Passwort zu kennen.
 */
function Geschuetzt({ children }: { children: React.ReactNode }) {
  const { status, wiederherstellung } = useSitzung();
  const ort = useLocation();

  if (status === 'laedt') {
    return <Laden />;
  }
  if (wiederherstellung) return <Navigate to="/password/new" replace />;
  if (status === 'abgemeldet') {
    const weiter = encodeURIComponent(ort.pathname + ort.search);
    return <Navigate to={`/login?next=${weiter}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Waehrend die Sitzung geprueft wird. Ein Lidschlag in der Regel — deshalb
 * kein Kasten mit Text, der aufblitzt, sondern ein ruhiger Kreisel.
 */
function Laden() {
  return (
    <main className="grid min-h-dvh place-items-center text-text-hell" role="status" aria-label={t('Sitzung wird geprüft', 'Checking your session')}>
      <Kreisel className="size-6" />
    </main>
  );
}

/** Angemeldet auf der Anmeldeseite: weiterschicken statt Formular zeigen. */
function NurAbgemeldet({ children }: { children: React.ReactNode }) {
  const { status, wiederherstellung } = useSitzung();
  if (status === 'laedt') {
    return <Laden />;
  }
  if (status === 'angemeldet' && !wiederherstellung) return <Navigate to={STANDARDZIEL} replace />;
  return <>{children}</>;
}

export default function App() {
  // Die Sprache als key: ein Wechsel baut Router und Seiten neu auf, die
  // Sitzung bleibt (sprache.ts, „Wechsel“).
  const sprache = useSprache();
  return (
    <SessionProvider>
      <SpracheAbgleich />
      <BrowserRouter key={sprache}>
        <Einwilligung />
        <Routes>
          <Route path="/" element={<Navigate to={STANDARDZIEL} replace />} />
          <Route path="/register" element={<NurAbgemeldet><Registrieren /></NurAbgemeldet>} />
          <Route path="/login" element={<NurAbgemeldet><Anmelden /></NurAbgemeldet>} />
          <Route path="/password" element={<NurAbgemeldet><PasswortVergessen /></NurAbgemeldet>} />
          {/* Ohne Riegel: die Seite entscheidet selbst, ob der Link noch traegt. */}
          <Route path="/password/new" element={<PasswortNeu />} />
          <Route path="/confirmed" element={<Bestaetigt />} />
          <Route path="/abgemeldet" element={<Abgemeldet />} />
          <Route path="/newsletter" element={<Newsletter />} />
          {/* Kündigungsbutton (§ 312k BGB, #271): ohne Anmeldung erreichbar. */}
          <Route path="/kuendigen" element={<Kuendigen />} />
          {/* Ohne Riegel, und das ist der Punkt: ein Bericht wird an Leute
              weitergeleitet, die hier kein Konto haben. Die Zustimmungsseite
              ist ein einmaliger Schritt in einem fremden Ablauf und entscheidet
              selbst, ob sie eine Anmeldung braucht. */}
          {/* Zwei Pfade, eine Seite: der Bericht liegt auf read.99takes.com,
              und ein englischer Host mit deutschem Pfad liest sich schief.
              /bericht bleibt, damit alte Links weiter funktionieren. */}
          <Route path="/bericht" element={<Bericht />} />
          <Route path="/report" element={<Bericht />} />
          <Route path="/connect" element={<Verbinden />} />
          <Route path="/verbinde" element={<Verbinden />} />
          {/* Fuenf Fragen nach der Registrierung, einmal je Konto. MitFragen
              schickt ein Konto ohne Antworten dorthin; ohne Tabelle nicht. */}
          <Route path="/welcome" element={<Geschuetzt><Fragen /></Geschuetzt>} />
          <Route path="/account" element={<Geschuetzt><MitFragen><Konto /></MitFragen></Geschuetzt>} />
          <Route path="/runs" element={<Geschuetzt><MitFragen><Laeufe /></MitFragen></Geschuetzt>} />
          <Route path="/runs/:id" element={<Geschuetzt><MitFragen><LaufDetail /></MitFragen></Geschuetzt>} />
          <Route path="/personas" element={<Geschuetzt><MitFragen><Personas /></MitFragen></Geschuetzt>} />
          <Route path="/keys" element={<Geschuetzt><MitFragen><Schluessel /></MitFragen></Geschuetzt>} />
          <Route path="/guide" element={<Geschuetzt><MitFragen><Anleitung /></MitFragen></Geschuetzt>} />
          {/* Admin-Ansicht (seit 16.10.2026): nur fuer Konten in SIM_ADMIN_KONTEN,
              der Server antwortet allen anderen mit 404. Ohne MitFragen: ein
              Admin soll nicht erst die Fragen der Registrierung beantworten. */}
          <Route path="/admin" element={<Geschuetzt><Admin /></Geschuetzt>} />
          <Route path="/admin/:id" element={<Geschuetzt><AdminKonto /></Geschuetzt>} />

          {/* Die deutschen Pfade von vor dem 25.09.2026, als Zweitweg.
              NICHT ueber den Auffang unten abhandeln: `Navigate` wirft Query und
              Fragment weg, und genau darin steht der Einmal-Code aus einer
              Bestaetigungs- oder Passwort-Mail. Eine verschickte Mail wuerde
              damit auf der Kontoseite landen statt den Code einzuloesen.
              Bei den uebrigen waere der Auffang zwar genug — sie hier
              mitzunehmen kostet eine Zeile und erspart die Unterscheidung. */}
          <Route path="/bestaetigt" element={<Bestaetigt />} />
          <Route path="/passwort/neu" element={<PasswortNeu />} />
          <Route path="/passwort" element={<NurAbgemeldet><PasswortVergessen /></NurAbgemeldet>} />
          <Route path="/registrieren" element={<NurAbgemeldet><Registrieren /></NurAbgemeldet>} />
          <Route path="/anmelden" element={<NurAbgemeldet><Anmelden /></NurAbgemeldet>} />
          <Route path="/konto" element={<Geschuetzt><MitFragen><Konto /></MitFragen></Geschuetzt>} />
          <Route path="/laeufe" element={<Geschuetzt><MitFragen><Laeufe /></MitFragen></Geschuetzt>} />
          <Route path="/schluessel" element={<Geschuetzt><MitFragen><Schluessel /></MitFragen></Geschuetzt>} />

          <Route path="*" element={<Navigate to={STANDARDZIEL} replace />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}
